import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import SubscriptionCheck from './SubscriptionCheck';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean; // If true, user must have active subscription
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireSubscription = true }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('dashboard');
  const currentLang = lang || 'tr';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">{t('protectedRoute.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url and language prefix
    return <Navigate to={`/${currentLang}/login`} state={{ from: location }} replace />;
  }

  // If subscription is required, wrap with SubscriptionCheck
  // SubscriptionCheck will handle social media setup check internally
  if (requireSubscription) {
    return (
      <SubscriptionCheck>
        {children}
      </SubscriptionCheck>
    );
  }

  // Without subscription requirement, render children directly
  return <>{children}</>;
};

export default ProtectedRoute;
